import { AnimatePresence, motion } from 'motion/react';
import { border, space, system } from 'styled-system';
import { noop } from 'lodash';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Flex } from '../grid';
import { H6, P } from '../typography/new';

const borderImages = system({
  borderImage: true,
});

const Wrapper = styled(Flex)`
  ${border};
  ${borderImages};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  border-top-width: 0px;
  opacity: ${({ disabled }) => (disabled ? 0.25 : 1)};

  &:last-child {
    border-bottom-width: 0px;
  }
`;

const Checkbox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  margin-right: 16px;
  background: ${({ checked, theme }) => (checked ? theme.colors.black : theme.colors.transparent)};
  border: 2px solid ${({ theme }) => theme.colors.black};
  border-radius: ${({ borderRadius }) => borderRadius};
  transition: 200ms background-color, border-color;
`;

const Radio = styled(Checkbox)`
  border-radius: 12px;
  background-color: transparent;
  border-color: ${({ theme }) => theme.colors.black};
  ${space};

  &:before {
    content: '';
    display: block;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background-color: ${({ selected, theme }) => (selected ? theme.colors.black : 'transparent')};
    transition: 200ms background-color;
  }
`;

const InputListItem = ({
  borderBottomColor,
  borderBottomStyle,
  borderBottomWidth,
  borderImage,
  children,
  disabled,
  label,
  labelProps,
  onSelect,
  py,
  selected,
  selectedHelperText,
  subItems,
}) => (
  <Wrapper
    borderBottomColor={borderBottomColor}
    borderBottomStyle={borderBottomStyle}
    borderBottomWidth={borderBottomWidth}
    borderImage={borderImage}
    disabled={disabled}
    justifyContent="space-between"
    onClick={disabled ? noop : onSelect}
    py={py}
  >
    <Flex flexDirection="column">
      <Flex flexDirection="row" flexGrow={1} alignItems="start">
        <Flex>
          <Radio selected={selected} />
        </Flex>

        <Flex flexDirection="column" flexGrow={1}>
          {label && (
            <Flex alignItems="center">
              <H6 my="0" {...labelProps}>
                {label}
              </H6>
            </Flex>
          )}
          {subItems}
        </Flex>
      </Flex>
      <AnimatePresence initial={false}>
        {selectedHelperText && selected && (
          <motion.div
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={{
              visible: {
                height: 'auto',
                opacity: 1,
                transition: {
                  type: 'tween',
                  mass: 0.5,
                  bounce: 0,
                },
              },
              hidden: {
                height: 0,
                opacity: 0,
                transition: {
                  type: 'tween',
                  mass: 0.5,
                  bounce: 0,
                },
              },
            }}
          >
            <P ml="40px" pt="4px" pr="16px" variant={4}>
              {selectedHelperText}
            </P>
          </motion.div>
        )}
      </AnimatePresence>
    </Flex>

    {children}
  </Wrapper>
);

InputListItem.propTypes = {
  borderBottomColor: PropTypes.string,
  borderBottomStyle: PropTypes.string,
  borderBottomWidth: PropTypes.string,
  borderImage: PropTypes.string,
  children: PropTypes.node,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  labelProps: PropTypes.shape({}),
  onSelect: PropTypes.func.isRequired,
  py: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  selected: PropTypes.bool,
  selectedHelperText: PropTypes.string,
  subItems: PropTypes.node,
  type: PropTypes.oneOf(['radio', 'checkbox']).isRequired,
  borderRadius: PropTypes.string,
};

InputListItem.defaultProps = {
  borderBottomColor: 'background',
  borderBottomStyle: 'solid',
  borderBottomWidth: '1px',
  borderImage: undefined,
  children: null,
  disabled: false,
  label: undefined,
  labelProps: undefined,
  py: '12px',
  selected: false,
  selectedHelperText: undefined,
  subItems: null,
  borderRadius: '50%',
};

export { InputListItem, Radio };
